import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import ModalComponent from "../../../../components/Modal";
import TextInput from "../../../../components/TextInput";
import {EditManageEmployeeSchema } from "../../../../components/Helpers";
import { updateEmployee as updateEmployeeService } from "../../../../Services/employee";
import CustomDropdown from "../../../../components/CustomDropdown";
import { toast } from "react-toastify";
import TextArea from "../../../../components/TextArea";

const EditEmployeeModal = ({
  onClose,
  show,
  title,
  initialValues,
  handleAfterSubmit,
}) => {
  const handleCloseModals = () => {
    onClose();
  };
  
  const id = initialValues?.id;
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Transform the initialValues to the format expected by the form
  const formattedInitialValues = {
    firstName: initialValues?.firstName || "",
    lastName: initialValues?.lastName || "",
    gender: initialValues?.gender === "M" ? "Male" : (initialValues?.gender === "F" ? "Female" : ""),
    email: initialValues?.email || "",
    phoneNumber: initialValues?.phoneNumber || "",
    technology: initialValues?.technology || "",
    status: initialValues?.status === "A" ? "Active" : "Deactive",
    company: initialValues?.company || " ",
    backgroundCheck: initialValues?.backgroundCheck || ""
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitError(null);
    setLoading(true);
    
    try {
      // Transform form data to match API request structure
      const employeeData = {
        fname: values.firstName,
        lname: values.lastName,
        gender: values.gender === "Male" ? "M" : "F",
        email: values.email,
        phone: values.phoneNumber,
        technology: values.technology,
        stsCd: values.status === "Active" ? "A" : "D",
        company: values.company || "DATACLOUD",
        backgroundCheck: values.backgroundCheck || ""
      };
      
      await updateEmployeeService(id, employeeData);
      
      // Success - close modal and refresh employee list
      toast.success("Employee updated successfully!");
      resetForm();
      handleCloseModals();
      
      // Call refreshEmployees to update the table
      if (typeof handleAfterSubmit === 'function') {
        handleAfterSubmit();
      }
    } catch (err) {
      let errorMessage = "Email already exists. Please use a different email address.";
      if (err && err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      setSubmitError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <ModalComponent show={show} onClose={onClose} title={title}>
      <Formik
        initialValues={formattedInitialValues}
        enableReinitialize
        validationSchema={EditManageEmployeeSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, handleChange, values, isSubmitting, setFieldValue }) => (
          <Form className="max-h-[85vh] overflow-y-auto">
            <div className="mt-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <TextInput
                  value={values.firstName}
                  name="firstName"
                  placeholder="First Name"
                  handleChange={(e) => {
                    handleChange(e);
                  }}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.firstName && touched.firstName && (
                  <div className="text-red-500 text-sm">{errors.firstName}</div>
                )}
              </div>
              {/* Last Name */}
              <div>
                <TextInput
                  value={values.lastName}
                  name="lastName"
                  placeholder="Last Name"
                  handleChange={(e) => {
                    handleChange(e);
                  }}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.lastName && touched.lastName && (
                  <div className="text-red-500 text-sm">{errors.lastName}</div>
                )}
              </div>
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                {/* Gender */}
                <div className="flex items-center space-x-4">
                  <label className="text-slate-400 text-base font-medium">
                    Gender:
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={values.gender === "Male"}
                    />
                    <span>Male</span>
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={values.gender === "Female"}
                    />
                    <span>Female</span>
                  </label>
                </div>
                {/* Status */}
                <div className="flex items-center space-x-4">
                  <label className="text-slate-400 text-base font-medium">
                    Status:
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="status"
                      value="Active"
                      checked={values.status === "Active"}
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="status"
                      value="Deactive"
                      checked={values.status === "Deactive"}
                    />
                    <span>Deactive</span>
                  </label>
                </div>
              </div>
              {/* Email */}
              <div>
                <TextInput
                  value={values.email}
                  name="email"
                  placeholder="Email"
                  handleChange={handleChange}
                  type="email"
                  classes="w-full"
                  legend
                />
                {errors.email && touched.email && (
                  <div className="text-red-500 text-sm">{errors.email}</div>
                )}
              </div>
              {/* Phone Number */}
              <div>
                <TextInput
                  value={values.phoneNumber}
                  name="phoneNumber"
                  placeholder="Phone Number"
                  handleChange={e => {
                    const allowed = e.target.value.replace(/[^\d\+\-\(\)\s]/g, "").slice(0, 13);
                    setFieldValue("phoneNumber", allowed);
                  }}
                  type="text"
                  classes="w-full"
                  legend
                />
                {/* Show phone validation error live while typing */}
                {errors.phoneNumber && (
                  <div className="text-red-500 text-sm">{errors.phoneNumber}</div>
                )}
              </div>
              {/* Technology */}
              <div>
                <TextInput
                  value={values.technology}
                  name="technology"
                  placeholder="Technology"
                  handleChange={(e) => {
                    handleChange(e);
                  }}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.technology && touched.technology && (
                  <div className="text-red-500 text-sm">{errors.technology}</div>
                )}
              </div>
              {/* Company Dropdown - Fixed onChange handler */}
              <div>
              <CustomDropdown
                value={
                  [
                    
                    { label: "UNISOFT", value: "UNISOFT" },
                    { label: "DATACLOUD", value: "DATACLOUD" },
                  ].find((user) => user?.value === values?.company) || null
                }
                name="company"
                handleChange={(selectedOption) => {
                  console.log(selectedOption,"OPTION")
                  // dispatch(setResetStateTimesheetById());
                  // dispatch(requestProjectsById(selectedOption?.id));
                  setFieldValue("company", selectedOption.value);
                  // setSelectedEmployeeId(selectedOption?.id);
                }}
                customClasses="w-full text-left"
                placeholder="Select Company"
                options={[
                  { label: "DATACLOUD", value: "DATACLOUD" },
                  { label: "UNISOFT", value: "UNISOFT" },
                ]}
                handleGetOptionValue={(option) => option.value}
                handleGetOptionName={(option) => option.label}
              />
                {errors.company && touched.company && (
                  <div className="text-red-500 text-sm">{errors.company}</div>
                )}
              </div>
              <div className="sm:col-span-2 flex flex-col items-start">
                <label className="block text-slate-400 text-sm mb-2">Background Check</label>
                <TextArea
                  value={values.backgroundCheck}
                  name="backgroundCheck"
                  placeholder=" "
                  handleChange={handleChange}
                  rows={3}
                  className="border rounded-lg w-full p-2"
                  classes="w-full"
                  style={{ minWidth: "100%", maxWidth: "100%" }}
                />
                {errors.backgroundCheck && touched.backgroundCheck && (
                  <div className="text-red-500 text-sm">{errors.backgroundCheck}</div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50  shadow-sm"
                onClick={handleCloseModals}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-[#201F1E] text-white h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-[#201F1E]"
                disabled={isSubmitting || loading}
              >
                {isSubmitting || loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalComponent>
  );
};

export default EditEmployeeModal;